import {
  groupRiskQuestions,
  GrowAccountOverview,
  GrowFund,
  GrowIncomeProjection,
  GrowPortfolioAllocation,
  GrowProject,
  GrowProjectStage,
  GrowProjectStatus,
  GrowTotalNetIncome,
  InyovaGrowPercentages,
  InyovaGrowRisk
} from '@inyova/grow';
import { GrowAccount, InyovaGrowAccountStatus, LoadState } from '@inyova/models';

import { Action, createReducer, createSelector, on } from '@ngrx/store';
import * as InyovaGrowActions from './inyova-grow.actions';
import * as AccountActions from '@account/account.actions';

import { calculatePortfolioAllocation } from '@grow/utils/inyova-grow.utils';
import { GrowProjectsFilters } from '@shared/models/Grow';
import { State } from '@shared/models/State';

export const GROW_REDUCER_FEATURE_KEY = 'grow';

export interface GrowState {
  growAccount: GrowAccount;
  growProjects: { [key: string]: GrowProject };
  growProjectsIds: string[];
  growProjectsLoading: boolean;
  growProjectsLoaded: boolean;
  growProjectsActiveFilter: GrowProjectsFilters;
  growFund: null | GrowFund;
  growRisk: null | InyovaGrowRisk;
  growPercentages: null | InyovaGrowPercentages;
  growRiskLoading: boolean;
  growRiskLoaded: boolean;
  growCheckLoading: boolean;
  growCheckLoaded: boolean;
  growSubmitLoading: boolean;
  growIntroLoading: boolean;
  growInvestmentLoading: boolean;
  growInvestmentDone: boolean;
  growInvestmentFinishStatus: 'success' | 'reached' | '';
  overviewLoadState: LoadState;
  overview: null | GrowAccountOverview;
  incomeProjectionLoadState: LoadState;
  incomeProjection: null | GrowIncomeProjection;
}

export const initialState: GrowState = {
  growAccount: {
    id: '',
    kind: 'grow',
    created_at: '',
    currency: 'CHF',
    customer_journey: {
      account_opened: true,
      account_funded: true,
      id_verification_completed: true,
      customer_funded: true,
      compliance_done: true,
      account_data_filled: true,
      ql_flow_completed: true
    },
    onboarding_step: 0,
    ql_onboarding_step: 0,
    deposit_qr_code: '',
    enabled_features: [],
    step_status: null,
    fees_consent: false,
    initial_investment: null,
    monthly_investment: null,
    stock_split: null,
    current_amount: 0,
    portfolio_value: null,
    portfolio_value_chf: null,
    portfolio_value_eur: null,
    portfolio_value_eur_chf: null,
    combined_expected_return: null,
    minimum_investment: 2000,
    iban: '',
    investment_risk_consent: false,
    owner_name: '',
    reporting_timestamp: '',
    inyova_and_custody_fees: null,
    product_costs: null,
    commitments_made: null,
    eur_exchange_rate: 0,
    savings_plan_social_proof: null
  },
  growProjects: {},
  growProjectsIds: [],
  growProjectsLoading: false,
  growProjectsLoaded: false,
  growProjectsActiveFilter: GrowProjectsFilters.ALL,
  growFund: null,
  growRisk: null,
  growPercentages: null,
  growRiskLoading: false,
  growRiskLoaded: false,
  growCheckLoading: false,
  growCheckLoaded: false,
  growSubmitLoading: false,
  growIntroLoading: false,
  growInvestmentLoading: false,
  growInvestmentDone: false,
  growInvestmentFinishStatus: '',
  overviewLoadState: 'INIT',
  overview: null,
  incomeProjectionLoadState: 'INIT',
  incomeProjection: null
};

const growReducer = createReducer(
  initialState,
  on(InyovaGrowActions.getInyovaGrowAccount, (state) => ({ ...state, growIntroLoading: false })),
  on(
    AccountActions.getAccountAfterGrowRiskUpdateSuccess,
    InyovaGrowActions.getInyovaGrowAccountSuccess,
    (state, action): GrowState => ({
      ...state,
      growAccount: { ...(action.data as unknown as GrowAccount) },
      ...(action.data.step_status === InyovaGrowAccountStatus.FUNDED && {
        growProjectsActiveFilter: (action.data as unknown as GrowAccount).commitments_made
          ? GrowProjectsFilters.INVESTABLE
          : GrowProjectsFilters.MY_PROJECTS
      })
    })
  ),
  on(InyovaGrowActions.createInyovaGrowAccount, (state) => ({
    ...state,
    growIntroLoading: true
  })),
  on(InyovaGrowActions.createInyovaGrowAccountSuccess, (state, action) => ({
    ...state,
    growAccount: action.data,
    growIntroLoading: false
  })),
  on(InyovaGrowActions.createInyovaGrowAccountFail, (state) => ({ ...state, growAccount: null, growIntroLoading: false })),
  on(InyovaGrowActions.getGrowIbanSuccess, (state, { iban, deposit_qr_code }) => ({
    ...state,
    growAccount: { ...state.growAccount, iban, deposit_qr_code }
  })),
  on(InyovaGrowActions.growPerformancePageOpened, InyovaGrowActions.getGrowProjects, (state) => ({
    ...state,
    growProjectsLoading: true,
    ...(state.overviewLoadState !== 'LOADED' && { overviewLoadState: 'LOADING' as LoadState }),
    ...(state.incomeProjectionLoadState !== 'LOADED' && { incomeProjectionLoadState: 'LOADING' as LoadState })
  })),
  on(InyovaGrowActions.getGrowAccountOverviewSuccess, (state, { overview }) => ({ ...state, overview, overviewLoadState: 'LOADED' as LoadState })),
  on(InyovaGrowActions.getGrowAccountOverviewFail, (state) => ({ ...state, overviewLoadState: { error: 'Failed to load overview' } })),
  on(InyovaGrowActions.getGrowIncomeProjectionSuccess, (state, { incomeProjection }) => ({
    ...state,
    incomeProjection,
    incomeProjectionLoadState: 'LOADED' as LoadState
  })),
  on(InyovaGrowActions.getGrowIncomeProjectionFail, (state) => ({
    ...state,
    incomeProjectionLoadState: { error: 'Failed to load income projection' }
  })),
  on(InyovaGrowActions.getGrowProjectsSuccess, (state, { projects, money_market_fund }) => {
    const growProjectsIds: string[] = [];
    const growProjects = projects.reduce((entities: { [key: string]: GrowProject }, project) => {
      growProjectsIds.push(project.id);
      return {
        ...entities,
        [project.id]: project
      };
    }, {});

    return {
      ...state,
      growProjects,
      growProjectsIds,
      growFund: money_market_fund,
      growProjectsLoading: false,
      growProjectsLoaded: true
    };
  }),
  on(InyovaGrowActions.getGrowProjectsFail, (state) => {
    return {
      ...state,
      growProjectsLoading: false,
      growProjectsLoaded: false
    };
  }),
  on(AccountActions.changeLanguage, (state) => ({
    ...state,
    growProjectsLoaded: false
  })),
  on(InyovaGrowActions.markGrowProjectSuccess, (state, { projectId, investment }) => {
    return {
      ...state,
      growProjects: {
        ...state.growProjects,
        [projectId]: {
          ...state.growProjects[projectId],
          investments: [investment]
        }
      }
    };
  }),
  on(InyovaGrowActions.unmarkGrowProjectSuccess, (state, { projectId }) => {
    return {
      ...state,
      growProjects: {
        ...state.growProjects,
        [projectId]: {
          ...state.growProjects[projectId],
          investments: []
        }
      }
    };
  }),
  on(InyovaGrowActions.markGrowProjectAsSeenSuccess, (state, { projectId }) => {
    return {
      ...state,
      growProjects: {
        ...state.growProjects,
        [projectId]: {
          ...state.growProjects[projectId],
          account_specific: {
            ...state.growProjects[projectId].account_specific,
            new: false
          }
        }
      }
    };
  }),
  on(InyovaGrowActions.filterGrowProjects, (state, { filter }) => ({ ...state, growProjectsActiveFilter: filter })),
  on(InyovaGrowActions.getGrowRisk, (state) => {
    return {
      ...state,
      growRiskLoading: true
    };
  }),
  on(InyovaGrowActions.getGrowRiskSuccess, (state, { data, percentages }) => {
    return {
      ...state,
      growRisk: data,
      growPercentages: percentages,
      growRiskLoading: false,
      growRiskLoaded: true,
      growCheckLoaded: state.growAccount.stock_split !== null
    };
  }),
  on(InyovaGrowActions.getGrowRiskFail, (state) => {
    return {
      ...state,
      growRiskLoading: false,
      growRiskLoaded: false
    };
  }),
  on(InyovaGrowActions.checkGrowRiskProfile, (state) => {
    return {
      ...state,
      growCheckLoading: true
    };
  }),
  on(InyovaGrowActions.checkGrowRiskProfileSuccess, (state, { data }) => {
    return {
      ...state,
      growPercentages: data,
      growCheckLoading: false,
      growCheckLoaded: true
    };
  }),
  on(InyovaGrowActions.checkGrowRiskProfileFail, (state) => {
    return {
      ...state,
      growCheckLoading: false,
      growCheckLoaded: false
    };
  }),
  on(InyovaGrowActions.updateInyovaGrowRisk, (state) => ({
    ...state,
    growSubmitLoading: true,
    growProjectsLoaded: false
  })),
  on(InyovaGrowActions.updateInyovaGrowRiskSuccess, (state, { data }) => {
    const { interest_account, ...risk } = data;

    return {
      ...state,
      growSubmitLoading: false,
      growRisk: {
        ...state.growRisk,
        ...risk
      },
      growCheckLoaded: interest_account.projects_percentage !== null
    };
  }),
  on(InyovaGrowActions.updateInyovaGrowRiskFail, (state) => ({
    ...state,
    growSubmitLoading: false
  })),
  on(InyovaGrowActions.finishGrowProjectInvestment, (state) => ({
    ...state,
    growInvestmentLoading: true
  })),
  on(InyovaGrowActions.finishGrowProjectInvestmentSuccess, (state, { projectId, amount }) => ({
    ...state,
    growInvestmentDone: true,
    growInvestmentFinishStatus: 'success' as const,
    growInvestmentLoading: false,
    growProjectsLoaded: false,
    growPortfolioAllocation: null,
    overviewLoadState: 'INIT' as LoadState,
    overview: null,
    totalNetIncome: null,
    incomeProjectionLoadState: 'INIT' as LoadState,
    incomeProjection: null,
    growAccount: { ...state.growAccount, commitments_made: true },
    growProjects: {
      ...state.growProjects,
      [projectId]: {
        ...state.growProjects[projectId],
        investments:
          state.growProjects[projectId].investments[0].status === GrowProjectStatus.INTERESTED
            ? [{ ...state.growProjects[projectId].investments[0], status: GrowProjectStatus.COMMITTED }]
            : [...state.growProjects[projectId].investments, { amount, status: GrowProjectStatus.COMMITTED }]
      }
    } as { [key: string]: GrowProject }
  })),
  on(InyovaGrowActions.finishGrowProjectInvestmentReached, (state) => ({
    ...state,
    growInvestmentDone: true,
    growInvestmentFinishStatus: 'reached' as const,
    growInvestmentLoading: false,
    growProjectsLoaded: false
  })),
  on(InyovaGrowActions.finishGrowProjectInvestmentFail, InyovaGrowActions.finishGrowProjectInvestmentReset, (state) => ({
    ...state,
    growInvestmentDone: false,
    growInvestmentSuccess: false,
    growInvestmentFinishStatus: '' as const,
    growInvestmentLoading: false
  })),
  // NgRx store clean up after logout
  on(AccountActions.customerLogout, () => ({ ...initialState })),
  on(AccountActions.customerLogoutAndUnpairDevice, () => ({ ...initialState }))
);

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function reducer(state: GrowState | undefined, action: Action) {
  return growReducer(state, action);
}

/*
 * SELECTORS
 * */
export const selectFeature = (state: State) => state.grow;
// Account reducer selectors
export const selectInyovaGrowAccount = createSelector(selectFeature, (state: GrowState) => state.growAccount);
export const selectInyovaGrowAccountID = createSelector(selectInyovaGrowAccount, (account) => account && account.id);
export const selectInyovaGrowAccountStatus = createSelector(selectInyovaGrowAccount, (account) => account.step_status);
export const selectInyovaGrowIntroLoading = createSelector(selectFeature, (state: GrowState) => state.growIntroLoading);

export const selectGrowAccountOverviewLoadState = createSelector(selectFeature, (state: GrowState) => state.overviewLoadState);
export const selectGrowAccountOverview = createSelector(selectFeature, (state: GrowState) => state.overview);
export const selectTotalNetIncome = createSelector(selectGrowAccountOverview, (overview): GrowTotalNetIncome => {
  if (overview === null) {
    return {
      received_interest: 0,
      accrued_interest_at_purchase: null,
      fees_paid: 0,
      income: 0,
      net_income: 0,
      projectBreakdown: [],
      mmf_profit: 0
    };
  }

  return {
    received_interest: overview.received_interest,
    accrued_interest_at_purchase: overview.accrued_interest_at_purchase,
    fees_paid: overview.fees_paid,
    income: overview.income,
    net_income: overview.net_income,
    mmf_profit: overview.mmf_profit,
    projectBreakdown: overview.investments
      .filter((investment) => investment.status === GrowProjectStatus.INVESTED && (investment.received_interest > 0 || investment.income > 0))
      .map((investment) => ({
        impact_project_title: investment.impact_project_title,
        received_interest: investment.received_interest,
        income: investment.income
      }))
  };
});

export const selectGrowIncomeProjectionLoadState = createSelector(selectFeature, (state: GrowState) => state.incomeProjectionLoadState);
export const selectGrowIncomeProjection = createSelector(selectFeature, (state: GrowState) => state.incomeProjection);

export const selectGrowProjectsLoaded = createSelector(selectFeature, (state: GrowState) => state.growProjectsLoaded);

export const selectGrowProjects = createSelector(selectFeature, (state: GrowState) => state.growProjects);

export const selectGrowProjectsIds = createSelector(selectFeature, (state: GrowState) => state.growProjectsIds);
export const selectGrowProjectsActiveFilter = createSelector(selectFeature, (state: GrowState) => state.growProjectsActiveFilter);

export const selectInyovaGrowPortfolioAllocation = createSelector(selectGrowAccountOverview, (accountOverview: GrowAccountOverview) => {
  const portfolioAllocation: GrowPortfolioAllocation = {
    investedInProjects: { percentage: 0, value: 0 },
    commitedToProjects: { percentage: 0, value: 0 },
    accruedInterest: { percentage: 0, value: 0 },
    cash: { percentage: 0, value: 0 }
  };

  if (accountOverview === null) return portfolioAllocation;

  return calculatePortfolioAllocation(
    accountOverview.total_amount,
    accountOverview.invested_amount,
    accountOverview.committed_amount,
    accountOverview.accrued_interest,
    accountOverview.cash_amount
  );
});

export const selectFilteredGrowProjectsIds = createSelector(
  selectGrowProjects,
  selectGrowProjectsIds,
  selectGrowProjectsActiveFilter,
  (growProjects, growProjectsIds, activeFilter) => {
    if (activeFilter === GrowProjectsFilters.ALL) {
      return growProjectsIds;
    }

    return growProjectsIds.filter((projectId) => {
      const currentProject = growProjects[projectId];
      const { stage, investments, account_specific } = currentProject;

      switch (activeFilter) {
        case GrowProjectsFilters.MY_PROJECTS: {
          const isInitialInterestOnly = investments.length === 1 && investments[0].status === GrowProjectStatus.INTERESTED;
          const isInactive = [GrowProjectStage.CLOSED, GrowProjectStage.CANCELLED].includes(stage);

          if (isInitialInterestOnly && isInactive) {
            return false;
          }

          const activeStatuses = [GrowProjectStatus.COMMITTED, GrowProjectStatus.INVESTED, GrowProjectStatus.INTERESTED];
          return investments.some((investment) => activeStatuses.includes(investment.status));
        }

        case GrowProjectsFilters.INVESTABLE:
          return stage === GrowProjectStage.INVESTABLE;

        case GrowProjectsFilters.NEW:
          return !!account_specific.new;

        case GrowProjectsFilters.RUNNING:
          return stage === GrowProjectStage.CLOSED;

        default:
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          false;
      }
    });
  }
);

export const selectGrowInvestedProjects = createSelector(selectGrowProjects, (entities) =>
  Object.values(entities).filter((project) => project.investments.some((investment) => investment.status === GrowProjectStatus.INVESTED))
);

export const selectGrowOpenProjectsIds = createSelector(selectGrowProjects, (projects) =>
  Object.values(projects)
    .filter((project) => [GrowProjectStage.INVESTABLE, GrowProjectStage.ANNOUNCED].includes(project.stage))
    .map((openProject) => openProject.id)
);

export const selectGrowProjectById = (id: string) =>
  createSelector(selectGrowProjects, (entities) => {
    return entities[id];
  });
export const selectInyovaGrowFund = createSelector(selectFeature, (state: GrowState) => state.growFund);
export const selectHasSelectedProject = createSelector(selectGrowProjects, (entities) => {
  return Object.values(entities).some((project) => project.investments.some((investment) => investment.status === GrowProjectStatus.INTERESTED));
});

export const selectHasInvestedFund = createSelector(selectInyovaGrowFund, (fund: GrowFund) => fund?.status === GrowProjectStatus.INVESTED);

export const selectInyovaGrowRiskLoaded = createSelector(selectFeature, (state: GrowState) => state.growRiskLoaded);
export const selectGrowRisk = createSelector(selectFeature, (state) => state.growRisk);
export const selectGrowOnboardingId = createSelector(selectGrowRisk, (state) => state.id);
export const selectInyovaGrowRisk = createSelector(selectGrowRisk, selectInyovaGrowAccount, (growRisk, growAccount) => {
  if (growRisk === null) {
    return { growAccount, surveyGroups: null, financialDetail: null };
  }
  return {
    growAccount,
    surveyGroups: groupRiskQuestions(growRisk.risk_survey.survey_questions),
    financialDetail: growRisk.financial_detail
  };
});
export const selectInyovaGrowPercentages = createSelector(selectFeature, (state) => state.growPercentages);

export const selectInyovaGrowCheckLoading = createSelector(selectFeature, (state: GrowState) => state.growCheckLoading);
export const selectInyovaGrowCheckLoaded = createSelector(selectFeature, (state: GrowState) => state.growCheckLoaded);
export const selectInyovaGrowSubmitLoading = createSelector(selectFeature, (state: GrowState) => state.growSubmitLoading);
export const selectInyovaGrowInvestmentLoading = createSelector(selectFeature, (state: GrowState) => state.growInvestmentLoading);
export const selectInyovaGrowInvestmentDone = createSelector(selectFeature, (state: GrowState) => state.growInvestmentDone);
export const selectInyovaGrowFinishStatus = createSelector(selectFeature, (state: GrowState) => state.growInvestmentFinishStatus);
export const selectInyovaGrowInvestmentDoneAndFinishStatus = createSelector(
  selectInyovaGrowInvestmentDone,
  selectInyovaGrowFinishStatus,
  (investmentDone, finishStatus) => ({ investmentDone, finishStatus })
);

export const selectGrowReporting = createSelector(
  selectGrowAccountOverviewLoadState,
  selectGrowAccountOverview,
  selectGrowIncomeProjectionLoadState,
  selectGrowIncomeProjection,
  (overviewLoadState, overview, incomeProjectionLoadState, incomeProjection) => {
    return { overviewLoadState, overview, incomeProjectionLoadState, incomeProjection };
  }
);

export const selectGrowPerformanceVm = createSelector(
  selectInyovaGrowAccount,
  selectInyovaGrowPortfolioAllocation,
  selectHasInvestedFund,
  selectInyovaGrowFund,
  selectGrowReporting,
  selectGrowProjectsActiveFilter,
  selectTotalNetIncome,
  (
    growAccount,
    portfolioAllocation,
    hasInvestedFund,
    fund,
    { overviewLoadState, overview, incomeProjectionLoadState, incomeProjection },
    projectsActiveFilter,
    totalNetIncome
  ) => {
    return {
      growAccount,
      portfolioAllocation,
      hasInvestedFund,
      fund,
      overviewLoadState,
      overview,
      incomeProjectionLoadState,
      incomeProjection,
      projectsActiveFilter,
      totalNetIncome
    };
  }
);
